import type { RenderContext } from './types';
import { useEffect, ReactElement, useRef, useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  type UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  SlotDateMode,
  useAppointment,
} from '@provider-directory/contexts/AppointmentContext';
import {
  useCachedSlots,
  CachedSlotsProvider,
} from '@provider-directory/contexts/CachedSlotsContext';
import { LiveSlotsProvider } from '@provider-directory/contexts/LiveSlotsContext';
import { NoAvailabilityScenarioProvider } from '@provider-directory/contexts/NoAvailabilityScenarioContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { SceneFormWithNavigation } from '../scene-form/scene-form.component';
import { ControlledAppointmentCalendar } from './fields/controlled-appointment-calendar.component';
import { ControlledAppointmentSlots } from './fields/controlled-appointment-slots.component';
import { ControlledLocationSelect } from './fields/controlled-location-selector.component';
import { AppointmentTimeFormFieldValues } from './select-appointment-form-values';

export interface SelectAppointmentSlotFormRawProps {
  methods: UseFormReturn<AppointmentTimeFormFieldValues>;
  reasonForVisit: string;
  onClose?: () => void;
  renderContext: RenderContext;
}

const SelectAppointmentSlotFormHeader = () => {
  const { t } = useTranslation();
  return (
    <h1 className={'font-serif text-display-large desktop:text-display-xlarge'}>
      {t('schedule.scheduleAnAppointment')}
    </h1>
  );
};

const SelectAppointmentSlotFormRaw = ({
  methods,
  reasonForVisit,
  onClose,
  renderContext,
}: SelectAppointmentSlotFormRawProps): ReactElement => {
  const { appointment } = useAppointment();
  const slotsRef = useRef<HTMLDivElement>(null);

  const {
    isLoading: slotsLoading,
    refetchProviderWithRFV,
    nearestAvailability,
  } = useCachedSlots();
  const { provider, isLoading: providerLoading } = useProvider();
  const [scrollToTimeSlots, setScrollToTimeSlots] = useState(false);
  // Disables the visit type and location selectors by default to prevent
  // animation-related issues that cause the options list to detach.
  const [isDisabled, setIsDisabled] = useState(true);
  const isLoading = providerLoading || slotsLoading;

  useEffect(() => {
    let slotDate = appointment.slotDate;
    if (
      appointment.slotDateMode === SlotDateMode.enum.nearest &&
      nearestAvailability?.byLocation &&
      appointment.departmentId
    ) {
      slotDate =
        nearestAvailability.byLocation[appointment.departmentId]?.date ??
        appointment.slotDate;
    }

    const formFields: AppointmentTimeFormFieldValues = {
      reasonForVisit,
      departmentId: appointment.departmentId ?? '',
      slotDate,
      slotTime: appointment.slotTime,
      visitTypeId: appointment.visitTypeId ?? '',
    };

    Object.entries(formFields).forEach(([key, value]): void => {
      methods.setValue(key as keyof AppointmentTimeFormFieldValues, value);
    });
  }, [appointment, methods, reasonForVisit, nearestAvailability]);

  useEffect(() => {
    if (reasonForVisit) {
      refetchProviderWithRFV(reasonForVisit);
    }
  }, [reasonForVisit, refetchProviderWithRFV]);

  const handleScrollToTimeSlots = () => {
    // Restores the visit type and location selectors.
    setIsDisabled(false);

    if (!scrollToTimeSlots) return;

    slotsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <ControlledLocationSelect isDisabled={isDisabled} isLoading={isLoading} />
      <ControlledAppointmentCalendar
        reasonForVisit={reasonForVisit}
        onChange={() => {
          setScrollToTimeSlots(true);
        }}
        shownByDefault={appointment.showCalendarByDefault}
      />
      <div ref={slotsRef}>
        <ControlledAppointmentSlots
          reasonForVisit={reasonForVisit}
          onAnimationComplete={handleScrollToTimeSlots}
          providerId={provider?.ehrExternalId?.replace('External|', '') ?? ''}
          providerLocations={provider?.locations ?? []}
          onClose={onClose}
          renderContext={renderContext}
        />
      </div>
    </>
  );
};
// This Live slots should use rfv from decision tree context
export const SelectAppointmentSlotFormRawWithData = (
  props: SelectAppointmentSlotFormRawProps,
): ReactElement => {
  const { provider } = useProvider();
  return (
    <NoAvailabilityScenarioProvider>
      <CachedSlotsProvider provider={provider}>
        <LiveSlotsProvider reasonForVisit={props.reasonForVisit}>
          <SelectAppointmentSlotFormRaw {...props} />
        </LiveSlotsProvider>
      </CachedSlotsProvider>
    </NoAvailabilityScenarioProvider>
  );
};

export interface SelectAppointmentSlotFormProps {
  onPrev: () => void;
  onNext: () => void;
  onClose?: () => void;
  reasonForVisit: string;
}

export const SelectAppointmentSlotForm = ({
  onPrev,
  onNext,
  onClose,
  reasonForVisit,
}: SelectAppointmentSlotFormProps): ReactElement => {
  const { provider } = useProvider();
  const { updateAppointment } = useAppointment();

  const methods = useForm<AppointmentTimeFormFieldValues>({
    mode: 'onSubmit',
  });
  const { handleSubmit } = methods;

  const onSubmit: SubmitHandler<AppointmentTimeFormFieldValues> = (data) => {
    if (!provider || !reasonForVisit) return;
    const slot = {
      ...data,
      visitTypeId: data.visitTypeId,
      slotDateMode: SlotDateMode.enum.selected,
    };

    updateAppointment(slot);
    onNext();
  };

  return (
    <FormProvider {...methods}>
      <SceneFormWithNavigation
        onSubmit={handleSubmit(onSubmit)}
        onPrev={onPrev}
        formId={'select-appointment-slot-form'}
      >
        <SelectAppointmentSlotFormHeader />
        <SelectAppointmentSlotFormRawWithData
          methods={methods}
          reasonForVisit={reasonForVisit}
          onClose={onClose}
          renderContext={'checkout-flow'}
        />
      </SceneFormWithNavigation>
    </FormProvider>
  );
};
